import { useState } from 'react'
import { Database, RefreshCw, AlertTriangle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  useTopQueries,
  useTableStats,
  useSlowLog,
  useDbPerfConfig,
  type TopQuerySort,
  type TopQuery,
  type TableStat,
  type SlowLogRow,
} from '@/hooks/useDbPerf'

// ─── KPI tile ────────────────────────────────────────────────────────────────

function KpiTile({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold tabular-nums ${warn ? 'text-amber-500' : ''}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Top Queries panel ────────────────────────────────────────────────────────

const SORT_LABELS: Record<TopQuerySort, string> = {
  total_exec_time: 'Total time',
  mean_exec_time: 'Mean time',
  calls: 'Calls',
}

function TopQueriesPanel({
  queries,
  sort,
  onSort,
  isLoading,
  isError,
}: {
  queries: TopQuery[] | undefined
  sort: TopQuerySort
  onSort: (s: TopQuerySort) => void
  isLoading: boolean
  isError: boolean
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="h-4 w-4" />
          Top Queries
        </CardTitle>
        <div className="flex gap-1">
          {(Object.keys(SORT_LABELS) as TopQuerySort[]).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={sort === s ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => onSort(s)}
            >
              {SORT_LABELS[s]}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading...</div>
        ) : isError ? (
          <div className="p-4 text-sm text-muted-foreground">
            pg_stat_statements unavailable (migrator pool or grant missing)
          </div>
        ) : !queries || queries.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No data</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-muted-foreground text-left">
                  <th className="px-4 py-2 font-medium">Query</th>
                  <th className="px-4 py-2 font-medium text-right">Calls</th>
                  <th className="px-4 py-2 font-medium text-right">Mean ms</th>
                  <th className="px-4 py-2 font-medium text-right">Max ms</th>
                  <th className="px-4 py-2 font-medium text-right">Total ms</th>
                </tr>
              </thead>
              <tbody>
                {queries.map((q) => (
                  <tr key={q.queryid} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-2 max-w-[300px]">
                      <span
                        className="font-mono truncate block max-w-[280px]"
                        title={q.query_fingerprint}
                      >
                        {q.query_fingerprint}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{q.calls.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {q.mean_exec_time_ms.toFixed(1)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {q.max_exec_time_ms.toFixed(1)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {q.total_exec_time_ms.toFixed(0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Table Stats panel ────────────────────────────────────────────────────────

function TableStatsPanel({
  stats,
  floor,
  isLoading,
  isError,
}: {
  stats: TableStat[] | undefined
  floor: number
  isLoading: boolean
  isError: boolean
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Table Stats</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading...</div>
        ) : isError ? (
          <div className="p-4 text-sm text-muted-foreground">
            pg_stat_user_tables unavailable
          </div>
        ) : !stats || stats.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No tables with &gt;1000 rows</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-muted-foreground text-left">
                  <th className="px-4 py-2 font-medium">Table</th>
                  <th className="px-4 py-2 font-medium text-right">Seq ratio</th>
                  <th className="px-4 py-2 font-medium text-right">Dead %</th>
                  <th className="px-4 py-2 font-medium text-right">Live rows</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((t) => {
                  const overFloor = t.seq_scan_ratio > floor
                  const deadPct = (t.dead_row_ratio * 100).toFixed(1)
                  return (
                    <tr key={t.table_name} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-2 font-mono">{t.table_name}</td>
                      <td className={`px-4 py-2 text-right tabular-nums ${overFloor ? 'text-amber-500 font-semibold' : ''}`}>
                        {(t.seq_scan_ratio * 100).toFixed(1)}%
                        {overFloor && <AlertTriangle className="inline h-3 w-3 ml-1" />}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{deadPct}%</td>
                      <td className="px-4 py-2 text-right tabular-nums">{t.n_live_tup.toLocaleString()}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Slow Query Log panel ─────────────────────────────────────────────────────

function SlowQueryLogPanel({
  rows,
  isLoading,
}: {
  rows: SlowLogRow[] | undefined
  isLoading: boolean
}) {
  const display = rows?.slice(0, 50) ?? []
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Slow Query Log (last 15m)</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading...</div>
        ) : display.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">
            No slow queries in last 15m (or Loki creds not set)
          </div>
        ) : (
          <ul className="divide-y divide-border max-h-64 overflow-y-auto">
            {display.map((row, i) => (
              <li key={i} className="px-4 py-2 text-xs hover:bg-muted/20">
                <div className="flex justify-between items-start gap-2">
                  <span className="font-mono truncate max-w-[60%]" title={row.query_fingerprint}>
                    {row.query_fingerprint ?? '(unknown query)'}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    {row.duration_ms != null && (
                      <Badge variant="outline" className="text-xs">
                        {row.duration_ms}ms
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="mt-0.5 flex gap-2 text-muted-foreground">
                  {row.route_pattern && <span>{row.route_pattern}</span>}
                  {row.worker_name && <span>{row.worker_name}</span>}
                  <span>{new Date(row.timestamp).toLocaleTimeString()}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AdminDbPerfPage() {
  const [sort, setSort] = useState<TopQuerySort>('total_exec_time')

  const topQueries = useTopQueries(sort)
  const tableStats = useTableStats()
  const slowLog = useSlowLog(200, '15m')
  const config = useDbPerfConfig()

  const ratioFloor = Number(config.data?.db_perf_seq_scan_ratio_floor ?? '0.5')
  const topMean = Math.round(topQueries.data?.[0]?.mean_exec_time_ms ?? 0)
  const tablesOver = tableStats.data?.filter((t) => t.seq_scan_ratio > ratioFloor).length ?? 0
  const tableCount = tableStats.data?.length ?? 0
  const slowCount = slowLog.data?.length ?? 0
  const slowRate = slowCount * 4 // 15m window × 4 = per-hour approx
  const totalDead = tableStats.data?.reduce((a, t) => a + t.n_dead_tup, 0) ?? 0
  const totalLive = tableStats.data?.reduce((a, t) => a + t.n_live_tup, 0) ?? 1
  const deadPct = ((totalDead / totalLive) * 100).toFixed(1)

  function handleRefreshAll() {
    void topQueries.refetch()
    void tableStats.refetch()
    void slowLog.refetch()
    void config.refetch()
  }

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto w-full">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">DB Perf</h1>
          <p className="text-sm text-muted-foreground">
            Slow queries, table scan ratios, and top statements from pg_stat_statements.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefreshAll}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* KPI rail */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile
          label="Top query mean"
          value={topMean > 0 ? `${topMean}ms` : '—'}
          warn={topMean > 500}
        />
        <KpiTile
          label="Slow rate (est. 1h)"
          value={slowCount > 0 ? String(slowRate) : '0'}
          warn={slowRate > 100}
        />
        <KpiTile
          label="Tables > ratio floor"
          value={tableCount > 0 ? `${tablesOver} / ${tableCount}` : '—'}
          warn={tablesOver > 0}
        />
        <KpiTile
          label="Dead row ratio"
          value={tableCount > 0 ? `${deadPct}%` : '—'}
          warn={parseFloat(deadPct) > 10}
        />
      </div>

      {/* Three panels */}
      <TopQueriesPanel
        queries={topQueries.data}
        sort={sort}
        onSort={setSort}
        isLoading={topQueries.isLoading}
        isError={topQueries.isError}
      />

      <TableStatsPanel
        stats={tableStats.data}
        floor={ratioFloor}
        isLoading={tableStats.isLoading}
        isError={tableStats.isError}
      />

      <SlowQueryLogPanel rows={slowLog.data} isLoading={slowLog.isLoading} />
    </div>
  )
}

export default AdminDbPerfPage
